// src/utils/msalInstance.ts
import {
    PublicClientApplication,
    EventType,
    AuthenticationResult,
    InteractionRequiredAuthError,
    BrowserConfigurationAuthError
} from '@azure/msal-browser';
import { msalConfig } from '../config/authConfig';
import { loginRequest } from '../config/authConfig';

class MSALSingleton {
    static getToken() {
        throw new Error('Method not implemented.');
    }
    static getAccount() {
        throw new Error('Method not implemented.');
    }
    private static instance: PublicClientApplication;
    private static initialized = false;
    private static inProgress = false;

public static getInstance(): PublicClientApplication {
        if (!MSALSingleton.instance) {
            // Enhanced configuration for mobile compatibility
            const config = {
                ...msalConfig,
                cache: {
                    ...msalConfig.cache,
                    cacheLocation: "localStorage",
                    storeAuthStateInCookie: true
                },
                system: {
                    ...msalConfig.system,
                    navigationClient: {
                        navigateInternal: async (url: string, options: any) => {
                            // Preserve hash during navigation
                            const urlInstance = new URL(url);
                            if (urlInstance.hash) {
                                sessionStorage.setItem('msal.preLoginHash', urlInstance.hash);
                            }
                            window.location.assign(url);
                            return false;
                        },
                        navigateExternal: async (url: string, options: any) => {
                            window.location.assign(url);
                            return false;
                        }
                    }
                }
            };
            
            MSALSingleton.instance = new PublicClientApplication(config);

            // Enhanced hash handling
            MSALSingleton.instance.addEventCallback((event) => {
                if (event.eventType === EventType.LOGIN_START) {
                    // Store the current hash if it exists
                    if (window.location.hash) {
                        sessionStorage.setItem('msal.preLoginHash', window.location.hash);
                    }
                }

                if (event.eventType === EventType.LOGIN_SUCCESS) {
                    const result = event.payload as AuthenticationResult;
                    if (result.account) {
                        try {
                            localStorage.setItem('msal.account', JSON.stringify(result.account));
                            window.dispatchEvent(new CustomEvent('msalLogin', { 
                                detail: result.account 
                            }));
                        } catch (e) {
                            console.warn('Failed to store account info:', e);
                        }
                    }
                    MSALSingleton.inProgress = false;

                    // Restore the previous hash if it existed
                    const previousHash = sessionStorage.getItem('msal.preLoginHash');
                    if (previousHash) {
                        window.location.hash = previousHash;
                        sessionStorage.removeItem('msal.preLoginHash');
                    }
                }

                if (event.eventType === EventType.LOGIN_FAILURE) {
                    // Handle hash empty error specifically
                    const error = event.error as BrowserConfigurationAuthError;
                    if (error && error.errorCode === 'hash_empty_error') {
                        // Attempt to recover the hash from storage
                        const storedHash = sessionStorage.getItem('msal.preLoginHash');
                        if (storedHash) {
                            // Reinitiate the login process with the recovered hash
                            MSALSingleton.instance.handleRedirectPromise(storedHash)
                                .catch(console.error);
                            return;
                        }
                    }
                    localStorage.removeItem('msal.account');
                    MSALSingleton.inProgress = false;
                    window.dispatchEvent(new CustomEvent('msalLogout'));
                }
            });
        }
        return MSALSingleton.instance;
    }

    public static async clearAllStorage(): Promise<void> {
        try {
            // Clear all MSAL-related items from localStorage
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('msal.') || key.includes('msal')) {
                    localStorage.removeItem(key);
                }
            });

            // Clear all MSAL-related items from sessionStorage
            Object.keys(sessionStorage).forEach(key => {
                if (key.startsWith('msal.') || key.includes('msal')) {
                    sessionStorage.removeItem(key);
                }
            });

            // Clear specific known items
            localStorage.removeItem('msal.account');
            sessionStorage.removeItem('msal.interaction.status');

            // Clear MSAL cookies
            document.cookie.split(';').forEach(cookie => {
                const [name] = cookie.split('=');
                if (name.trim().startsWith('msal.')) {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                }
            });

            MSALSingleton.inProgress = false;

            // Dispatch logout event
            window.dispatchEvent(new CustomEvent('msalLogout'));
            
            console.log('All MSAL storage cleared');
        } catch (e) {
            console.error('Error clearing MSAL storage:', e);
        }
    }

    public static isInitialized(): boolean {
        return MSALSingleton.initialized;
    }

    public static async refreshToken(): Promise<void> {

        const accounts = MSALSingleton.instance.getAllAccounts();

        try {
            const silentResult = await MSALSingleton.instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            });
            
            if (silentResult) {
                // Update localStorage with the current account
                localStorage.setItem('msal.account', JSON.stringify(accounts[0]));
                // Dispatch event to notify UI of successful refresh
                window.dispatchEvent(new CustomEvent('msalLogin', { 
                    detail: accounts[0] 
                }));
            }
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Clear storage if interactive login is required
                localStorage.removeItem('msal.account');
                MSALSingleton.clearAllStorage();
                // Notify UI of logout
                window.dispatchEvent(new CustomEvent('msalLogout'));
            }
            throw error;
        }
    }

    public static async initialize(): Promise<void> {
        if (!MSALSingleton.initialized) {
            const instance = MSALSingleton.getInstance();
            
            // Attempt to restore account from localStorage
            const savedAccount = localStorage.getItem('msal.account');
            if (savedAccount) {
                try {
                    const account = JSON.parse(savedAccount);
                    // Verify the account still exists in MSAL
                    const accounts = instance.getAllAccounts();
                    if (!accounts.some(a => a.homeAccountId === account.homeAccountId)) {
                        localStorage.removeItem('msal.account');
                    }
                } catch (e) {
                    localStorage.removeItem('msal.account');
                }
            }

            await instance.initialize();
            MSALSingleton.initialized = true;

            // Try to refresh token on initialization
            try {
                await MSALSingleton.refreshToken();
            } catch (e) {
                console.warn('Token refresh failed on initialization');
                localStorage.removeItem('msal.account');
            }
        }
    }

    public static async checkAndWaitForInteraction(): Promise<void> {
        if (MSALSingleton.inProgress) {
            // Wait a bit to allow any existing interaction to complete
            await new Promise(resolve => setTimeout(resolve, 1000));

            if (MSALSingleton.inProgress) {
                throw new Error('interaction_in_progress: Another authentication attempt is in progress');
            }
        }
    }

    public static setInteractionInProgress(value: boolean): void {
        MSALSingleton.inProgress = value;
    }
}

// Add cleanup on page unload
window.addEventListener('unload', () => {
    MSALSingleton.clearAllStorage();
});

export default MSALSingleton;